<template>
  <div class="mod-category-tag">
    <div class="page-header">
      <span class="page-header__title">分类标签</span>
    </div>

    <div class="page-body">
      <!-- 筛选栏 -->
      <div class="toolbar">
        <el-input
          v-model="dataForm.keyword"
          placeholder="搜索标签..."
          clearable
          prefix-icon="el-icon-search"
          class="toolbar__search"
          @input="handleSearch">
        </el-input>
        <span class="toolbar__tip">搜索只作用于下方列表，预览始终展示全部标签</span>
        <div class="toolbar__right">
          <el-button icon="el-icon-refresh" @click="loadList">刷新</el-button>
          <el-button type="primary" icon="el-icon-plus" @click="openDialog()">新增标签</el-button>
        </div>
      </div>

      <!-- H5 首页标签预览 -->
      <div class="panel">
        <div class="panel__header">
          <span>H5 首页标签预览（7列网格）</span>
          <div class="panel__header-extra">
            <span class="panel__tip">共 {{ gridTags.length }} 个</span>
            <template v-if="sortDirty">
              <span v-if="sortSaving" class="panel__tip">保存中 {{ savingDone }}/{{ savingTotal }}</span>
              <el-button type="text" :disabled="sortSaving" @click="resetSort">取消</el-button>
              <el-button type="primary" size="mini" :loading="sortSaving" @click="saveSort">保存排序</el-button>
            </template>
            <span v-else class="panel__tip">拖拽可调整排序</span>
          </div>
        </div>
        <div class="panel__body" v-loading="loading">
          <div class="tag-grid">
            <div
              v-for="(tag, index) in gridTags"
              :key="tag.id"
              class="tag-grid__item"
              :class="{ 'is-dragging': dragIndex === index, 'is-disabled': !isEnabled(tag) }"
              draggable="true"
              @dragstart="onDragStart(index)"
              @dragover.prevent="onDragOver(index)"
              @dragend="onDragEnd">
              {{ tag.name }}
            </div>
          </div>
          <div v-if="!gridTags.length && !loading" class="empty-tip">暂无标签</div>
        </div>
      </div>

      <!-- 标签列表 -->
      <div class="panel">
        <div class="panel__header">
          <span>标签列表</span>
        </div>
        <div class="panel__body panel__body--table">
          <el-table :data="pagedTags" v-loading="loading" style="width: 100%;">
            <el-table-column prop="sort" label="排序" width="90"></el-table-column>
            <el-table-column prop="name" label="标签名" min-width="160"></el-table-column>
            <el-table-column prop="keywords" label="标签关键词" min-width="240" show-overflow-tooltip>
              <template slot-scope="scope">
                <span v-if="scope.row.keywords">{{ scope.row.keywords }}</span>
                <span v-else class="text-placeholder">未设置</span>
              </template>
            </el-table-column>
            <el-table-column prop="movieCount" label="关联视频数" width="120">
              <template slot-scope="scope">{{ scope.row.movieCount || 0 }}</template>
            </el-table-column>
            <el-table-column label="状态" width="100">
              <template slot-scope="scope">
                <el-tag :type="isEnabled(scope.row) ? 'success' : 'info'" size="small">
                  {{ isEnabled(scope.row) ? '正常' : '禁用' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="createdAt" label="创建时间" min-width="170">
              <template slot-scope="scope">{{ formatTime(scope.row.createdAt) }}</template>
            </el-table-column>
            <el-table-column label="操作" width="180" fixed="right">
              <template slot-scope="scope">
                <el-button type="text" size="small" @click="openDialog(scope.row)">编辑</el-button>
                <el-button
                  type="text"
                  size="small"
                  :class="isEnabled(scope.row) ? 'danger-text' : ''"
                  @click="handleToggleStatus(scope.row)">
                  {{ isEnabled(scope.row) ? '禁用' : '启用' }}
                </el-button>
                <el-button type="text" size="small" class="danger-text" @click="handleDelete(scope.row)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
          <el-pagination
            :current-page="page"
            :page-sizes="[20, 50, 100]"
            :page-size="limit"
            :total="filteredTags.length"
            layout="total, sizes, prev, pager, next, jumper"
            class="pagination"
            @size-change="handleSizeChange"
            @current-change="handlePageChange">
          </el-pagination>
        </div>
      </div>
    </div>

    <!-- 弹窗：新增 / 编辑标签 -->
    <el-dialog
      :visible.sync="dialogVisible"
      :title="dataFormDialog.id ? '编辑标签' : '新增标签'"
      width="480px"
      :close-on-click-modal="false">
      <el-form :model="dataFormDialog" :rules="dataRule" ref="dialogForm" label-position="top">
        <el-form-item label="标签名" prop="name">
          <el-input
            v-model="dataFormDialog.name"
            placeholder="最长 50 字符，不能重复"
            maxlength="50"
            show-word-limit>
          </el-input>
        </el-form-item>
        <el-form-item label="标签关键词">
          <div class="words-box">
            <!-- 标签名永远参与匹配，做成不可删除的只读块钉在最前面 -->
            <el-tag class="words-box__self" size="medium" type="info" :disable-transitions="true">
              {{ dataFormDialog.name || '标签名' }}
              <span class="words-box__always">始终匹配</span>
            </el-tag>

            <el-tag
              v-for="(word, index) in keywordList"
              :key="word"
              class="words-box__word"
              :class="{ 'is-redundant': isRedundant(word) }"
              size="medium"
              closable
              :disable-transitions="true"
              @close="removeWord(index)">
              {{ word }}
              <i v-if="isRedundant(word)" class="el-icon-warning-outline"></i>
            </el-tag>

            <el-input
              v-if="wordInputVisible"
              ref="wordInput"
              v-model="wordInput"
              class="words-box__input"
              size="mini"
              @keyup.enter.native="commitWords"
              @keyup.delete.native="backspaceRemove"
              @blur="commitWords">
            </el-input>
            <el-button v-else class="words-box__add" size="mini" @click="showWordInput">+ 添加关键词</el-button>
          </div>

          <div class="form-tip">
            这个标签除了标签名还认哪些关键词。输入后回车添加，也可以一次粘贴多个（逗号、顿号、分号都能分开）。
            <span v-if="redundantWords.length" class="form-tip--warn">
              「{{ redundantWords.join('、') }}」里已经包含标签名「{{ dataFormDialog.name }}」，
              光靠标签名就能命中，这{{ redundantWords.length > 1 ? '些' : '条' }}不会生效。
            </span>
          </div>
        </el-form-item>

        <el-form-item label="试一下">
          <el-input
            v-model="previewTitle"
            placeholder="贴一个视频标题，看会命中哪些标签"
            clearable
            @input="schedulePreview">
            <i v-if="previewLoading" slot="suffix" class="el-icon-loading preview__spinner"></i>
          </el-input>
          <div class="preview">
            <template v-if="previewHits.length">
              <el-tag
                v-for="hit in previewHits"
                :key="hit.tagId"
                size="small"
                :type="hit.tagName === dataFormDialog.name ? 'success' : 'info'"
                :disable-transitions="true">
                {{ hit.tagName }}
                <span class="preview__why">{{ hit.matchedBy === 'name' ? '标签名' : hit.matchedValue }}</span>
              </el-tag>
            </template>
            <span v-else-if="previewTitle && !previewLoading" class="preview__empty">没有命中任何标签</span>
          </div>
          <div class="form-tip">
            按<b>已保存</b>的标签数据试跑，弹窗里还没保存的改动不会体现；保存后会自动生效。
          </div>
        </el-form-item>
        <el-form-item label="排序" prop="sort">
          <el-input-number v-model="dataFormDialog.sort" :min="0" :max="999999" style="width: 100%;"></el-input-number>
          <div class="form-tip">数值大的排在前面，自动打标签时也按这个顺序取前 3 个</div>
        </el-form-item>
      </el-form>
      <template slot="footer">
        <el-button type="primary" :loading="submitting" @click="handleSubmit">保存</el-button>
        <el-button @click="dialogVisible = false">取消</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import {
  getMovieTagPage,
  addMovieTag,
  updateMovieTag,
  updateMovieTagSort,
  updateMovieTagStatus,
  deleteMovieTag,
  previewMovieTagKeywords,
  reloadMovieTagKeywords,
  TAG_STATUS
} from '@/api/movie'

// 关键词分隔符，和后端 TagKeywordService.SEPARATOR 保持一致。
// 不切空格——「gay sex」「AI 换脸」这类词里本来就带空格，切了就废了
const WORD_SEPARATOR = /[,，、;；]+/

// 排序保存时每批并发的请求数，标签较多时避免一次性打满
const SAVE_BATCH_SIZE = 8
// 一次性拉取的标签上限：H5 预览需要展示全部标签，不能按页取
const FETCH_LIMIT = 1000

export default {
  name: 'CategoryTag',
  data () {
    return {
      loading: false,
      submitting: false,
      // 全量标签，H5 预览直接用它，下方表格在前端做筛选和分页
      allTags: [],
      page: 1,
      limit: 20,
      dataForm: {
        keyword: ''
      },
      // 拖拽排序
      gridTags: [],
      dragIndex: -1,
      sortDirty: false,
      sortSaving: false,
      savingTotal: 0,
      savingDone: 0,
      // 弹窗
      dialogVisible: false,
      dataFormDialog: {
        id: '',
        name: '',
        sort: 100
      },
      // 关键词按 chip 编辑，保存时再拼回逗号分隔的字符串
      keywordList: [],
      wordInput: '',
      wordInputVisible: false,
      // 试一下
      previewTitle: '',
      previewHits: [],
      previewLoading: false,
      previewTimer: null,
      previewSeq: 0
    }
  },

  beforeDestroy () {
    clearTimeout(this.previewTimer)
  },

  computed: {
    /** 已经被标签名覆盖、写了也不会生效的关键词 */
    redundantWords () {
      return this.keywordList.filter(word => this.isRedundant(word))
    },
    // 表格数据：关键字在前端过滤，保证筛选作用于全部标签而不是某一页
    filteredTags () {
      const keyword = this.dataForm.keyword.trim().toLowerCase()
      if (!keyword) {
        return this.allTags
      }
      return this.allTags.filter(item => String(item.name || '').toLowerCase().includes(keyword))
    },
    pagedTags () {
      const start = (this.page - 1) * this.limit
      return this.filteredTags.slice(start, start + this.limit)
    },
    dataRule () {
      return {
        name: [
          { required: true, message: '标签名不能为空', trigger: 'blur' }
        ],
        sort: [
          { required: true, message: '请填写排序', trigger: 'blur' }
        ]
      }
    }
  },

  created () {
    this.loadList()
  },

  methods: {
    // H5 预览要展示全部标签，因此一次性拉全量，筛选和分页都在前端做
    loadList () {
      this.loading = true
      return getMovieTagPage({ page: 1, limit: FETCH_LIMIT }).then(({ data: res }) => {
        if (res.code !== 0) {
          this.allTags = []
          return this.$message.error(res.msg)
        }
        this.allTags = (res.data && res.data.list) || []
        const total = (res.data && res.data.total) || 0
        if (total > FETCH_LIMIT) {
          this.$message.warning(`标签共 ${total} 个，当前只加载了前 ${FETCH_LIMIT} 个`)
        }
        this.resetSort()
      }).catch(() => {
        this.$message.error('加载标签失败')
      }).finally(() => {
        this.loading = false
      })
    },

    handleSearch () {
      this.page = 1
    },

    handleSizeChange (val) {
      this.limit = val
      this.page = 1
    },

    handlePageChange (val) {
      this.page = val
    },

    // ---------- 首页预览拖拽排序 ----------
    // 接口按 sort 降序返回，序号越大越靠前
    resetSort () {
      this.gridTags = [...this.allTags]
        .sort((a, b) => (Number(b.sort) || 0) - (Number(a.sort) || 0))
      this.sortDirty = false
      this.dragIndex = -1
    },

    onDragStart (index) {
      this.dragIndex = index
    },

    onDragOver (index) {
      if (this.dragIndex === -1 || this.dragIndex === index) {
        return
      }
      const list = [...this.gridTags]
      const [moved] = list.splice(this.dragIndex, 1)
      list.splice(index, 0, moved)
      this.gridTags = list
      this.dragIndex = index
      this.sortDirty = true
    },

    onDragEnd () {
      this.dragIndex = -1
    },

    // 拖拽后按新顺序重新分配 sort：排在最前的值最大
    async saveSort () {
      const count = this.gridTags.length
      const changed = this.gridTags
        .map((tag, index) => ({ tag, sort: count - index }))
        .filter(item => Number(item.tag.sort) !== item.sort)

      if (!changed.length) {
        this.sortDirty = false
        return this.$message.info('排序未发生变化')
      }

      this.sortSaving = true
      this.savingTotal = changed.length
      this.savingDone = 0
      try {
        // 后端只有单条排序接口，分批并发避免请求排长队
        for (let i = 0; i < changed.length; i += SAVE_BATCH_SIZE) {
          const batch = changed.slice(i, i + SAVE_BATCH_SIZE)
          const results = await Promise.all(
            batch.map(item => updateMovieTagSort(item.tag.id, item.sort))
          )
          const failed = results.find(({ data: res }) => res.code !== 0)
          if (failed) {
            throw new Error(failed.data.msg || '保存排序失败')
          }
          this.savingDone += batch.length
        }
        this.$message.success(`已更新 ${changed.length} 个标签的排序`)
        this.sortDirty = false
        await this.loadList()
      } catch (e) {
        console.error('保存排序失败:', e)
        this.$message.error(e.message || '保存排序失败')
      } finally {
        this.sortSaving = false
        this.savingTotal = 0
        this.savingDone = 0
      }
    },

    // ---------- 增删改 ----------
    openDialog (row) {
      this.dialogVisible = true
      this.keywordList = this.parseWords(row ? row.keywords : '')
      this.wordInput = ''
      this.wordInputVisible = false
      this.resetPreview()
      this.$nextTick(() => {
        this.$refs.dialogForm.clearValidate()
        this.dataFormDialog = row
          ? { id: row.id, name: row.name, sort: Number(row.sort) || 0 }
          : { id: '', name: '', sort: 100 }
      })
    },

    // ---------- 关键词 chip ----------
    parseWords (raw) {
      if (!raw) {
        return []
      }
      const seen = new Set()
      return raw.split(WORD_SEPARATOR)
        .map(word => word.trim())
        .filter(word => word && !seen.has(word) && seen.add(word))
    },

    /** 词里含了标签名 -> 光靠标签名就能命中，这条永远轮不到 */
    isRedundant (word) {
      const name = (this.dataFormDialog.name || '').trim().toLowerCase()
      return !!name && word.toLowerCase().includes(name)
    },

    showWordInput () {
      this.wordInputVisible = true
      this.$nextTick(() => {
        const input = this.$refs.wordInput
        if (input) {
          input.focus()
        }
      })
    },

    /** 回车或失焦时收词，支持一次粘贴多个 */
    commitWords () {
      const raw = this.wordInput.trim()
      this.wordInput = ''
      this.wordInputVisible = false
      if (!raw) {
        return
      }
      const added = []
      this.parseWords(raw).forEach(word => {
        if (word.length > 50) {
          return this.$message.warning(`「${word.slice(0, 12)}…」太长了，单个关键词不要超过 50 字`)
        }
        if (this.keywordList.includes(word)) {
          return this.$message.warning(`「${word}」已经在列表里了`)
        }
        added.push(word)
      })
      if (!added.length) {
        return
      }
      const next = this.keywordList.concat(added)
      if (next.join('，').length > 500) {
        return this.$message.warning('关键词总长度不能超过 500 字')
      }
      this.keywordList = next
    },

    /** 输入框为空时按退格，删掉最后一个词 */
    backspaceRemove () {
      if (!this.wordInput && this.keywordList.length) {
        this.keywordList = this.keywordList.slice(0, -1)
      }
    },

    removeWord (index) {
      this.keywordList = this.keywordList.filter((_, i) => i !== index)
    },

    // ---------- 试一下 ----------
    resetPreview () {
      clearTimeout(this.previewTimer)
      this.previewTitle = ''
      this.previewHits = []
      this.previewLoading = false
    },

    schedulePreview () {
      clearTimeout(this.previewTimer)
      const title = this.previewTitle.trim()
      if (!title) {
        this.previewHits = []
        this.previewLoading = false
        return
      }
      this.previewLoading = true
      this.previewTimer = setTimeout(this.runPreview, 350)
    },

    runPreview () {
      const title = this.previewTitle.trim()
      if (!title) {
        return
      }
      // 请求可能乱序回来，只认最后一次
      const seq = ++this.previewSeq
      previewMovieTagKeywords(title).then(({ data: res }) => {
        if (seq !== this.previewSeq) {
          return
        }
        this.previewHits = res.code === 0 ? (res.data || []) : []
        if (res.code !== 0) {
          this.$message.error(res.msg)
        }
      }).catch(() => {
        if (seq === this.previewSeq) {
          this.previewHits = []
        }
      }).finally(() => {
        if (seq === this.previewSeq) {
          this.previewLoading = false
        }
      })
    },

    handleSubmit () {
      this.$refs.dialogForm.validate(valid => {
        if (!valid) {
          return false
        }
        this.submitting = true
        // 还没收进 chip 的输入也一并保存，免得用户以为丢了
        this.commitWords()
        // 空串表示清空该标签的关键词，所以这里不做 undefined 兜底
        const keywords = this.keywordList.join('，')
        const request = this.dataFormDialog.id
          ? updateMovieTag(this.dataFormDialog.id, {
            name: this.dataFormDialog.name,
            keywords,
            sort: this.dataFormDialog.sort
          })
          : addMovieTag({
            name: this.dataFormDialog.name,
            keywords,
            sort: this.dataFormDialog.sort
          })

        request.then(({ data: res }) => {
          if (res.code !== 0) {
            return this.$message.error(res.msg)
          }
          this.$message.success(this.$t('prompt.success'))
          this.dialogVisible = false
          this.loadList()
          // 后端标签缓存默认 300 秒，不刷一下的话改完的关键词要等几分钟才参与匹配
          reloadMovieTagKeywords().catch(() => {})
        }).catch(() => {
          this.$message.error('保存失败')
        }).finally(() => {
          this.submitting = false
        })
      })
    },

    handleToggleStatus (row) {
      const enabled = this.isEnabled(row)
      const nextStatus = enabled ? TAG_STATUS.DISABLED : TAG_STATUS.NORMAL
      updateMovieTagStatus(row.id, nextStatus).then(({ data: res }) => {
        if (res.code !== 0) {
          return this.$message.error(res.msg)
        }
        this.$message.success(enabled ? '已禁用' : '已启用')
        this.loadList()
      }).catch(() => {
        this.$message.error('操作失败')
      })
    },

    handleDelete (row) {
      const tip = row.movieCount
        ? `「${row.name}」已关联 ${row.movieCount} 部影片，仍有关联时后端会拒绝删除。确定继续？`
        : `确定删除标签「${row.name}」？`
      this.$confirm(tip, '提示', {
        type: 'warning'
      }).then(() => {
        return deleteMovieTag(row.id)
      }).then(({ data: res }) => {
        if (res.code !== 0) {
          return Promise.reject(new Error(res.msg || '删除失败'))
        }
        this.$message.success('删除成功')
        this.loadList()
      }).catch(e => {
        if (e && e.message) {
          this.$message.error(e.message)
        }
      })
    },

    // ---------- 展示 ----------
    isEnabled (row) {
      return Number(row.status) !== TAG_STATUS.DISABLED
    },

    // 接口返回的是 ISO 时间，截断到分钟展示
    formatTime (value) {
      if (!value) {
        return '—'
      }
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) {
        return value
      }
      const pad = num => String(num).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
    }
  }
}
</script>

<style lang="scss" scoped>
.mod-category-tag {
  background-color: #f5f7fa;
  min-height: 100%;
}

.page-header {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  background-color: #fff;
  border-bottom: 1px solid #ebeef5;

  &__title {
    font-size: 16px;
    font-weight: 600;
    color: #1f2d3d;
  }
}

.page-body {
  padding: 16px;
}

.toolbar {
  display: flex;
  align-items: center;
  margin-bottom: 16px;

  &__search {
    width: 260px;
    margin-right: 12px;
  }

  &__tip {
    font-size: 12px;
    color: #909399;
  }

  &__right {
    margin-left: auto;
  }
}

.panel {
  background-color: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  margin-bottom: 16px;

  &__header {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid #ebeef5;
    font-size: 14px;
    font-weight: 600;
    color: #1f2d3d;
  }

  &__header-extra {
    display: flex;
    align-items: center;
    margin-left: auto;

    .el-button + .el-button {
      margin-left: 10px;
    }
  }

  &__tip {
    font-size: 13px;
    font-weight: 400;
    color: #909399;

    & + & {
      margin-left: 10px;
    }
  }

  &__body {
    padding: 16px;

    &--table {
      padding: 0 16px 16px;
    }
  }
}

.tag-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;

  &__item {
    height: 30px;
    line-height: 30px;
    text-align: center;
    background-color: #3a73e6c7;
    color: #fff;
    border-radius: 3px;
    font-size: 12px;
    cursor: move;
    user-select: none;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding: 0 6px;
    transition: opacity .2s;

    &:hover {
      opacity: .85;
    }

    &.is-dragging {
      opacity: .4;
    }

    // 已禁用的标签在预览里灰显，便于分辨
    &.is-disabled {
      background-color: #c0c4cc;
    }
  }
}

.empty-tip {
  text-align: center;
  color: #909399;
  font-size: 14px;
  padding: 16px 0;
}

.danger-text {
  color: #f56c6c;
}

.form-tip {
  color: #909399;
  font-size: 12px;
  line-height: 1.5;
  margin-top: 4px;
}

.text-placeholder {
  color: #c0c4cc;
}

.form-tip--warn {
  color: #e6a23c;
  display: block;
  margin-top: 2px;
}

/* 关键词 chip 编辑区 */
.words-box {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-height: 40px;
  padding: 6px 8px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;

  &__self {
    border-style: dashed;
    cursor: default;
  }

  &__always {
    margin-left: 4px;
    font-size: 11px;
    opacity: 0.7;
  }

  &__word.is-redundant {
    color: #e6a23c;
    background: #fdf6ec;
    border-color: #f5dab1;
  }

  &__input {
    width: 130px;
  }

  &__add {
    padding: 4px 8px;
  }
}

/* 试一下 */
.preview {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-height: 24px;
  margin-top: 8px;

  &__why {
    margin-left: 4px;
    opacity: 0.65;
    font-size: 11px;
  }

  &__empty {
    color: #909399;
    font-size: 12px;
  }

  &__spinner {
    line-height: 40px;
  }
}

.pagination {
  margin-top: 16px;
  text-align: right;
}

::v-deep .el-table th {
  background-color: #fafbfc;
  color: #909399;
  font-weight: 500;
}
</style>
